import { encodePlayer, packVec3 } from "@dust/world/internal";
import IWorldAbi from "@dust/world/out/IWorld.sol/IWorld.abi";
import { resourceToHex } from "@latticexyz/common";
import { decodeTransactionError } from "@permutation-city/common";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useDustClient } from "../../../hooks/useDustClient";
import { useCursorQuery } from "../../../queries/useCursorQuery";
import {
  cityForceFieldId,
  useForceFieldQuery,
} from "../../../queries/useForceFieldQuery";
import { usePlayerInventoryQuery } from "../../../queries/usePlayerInventoryQuery";
import { usePlayerPositionQuery } from "../../../queries/usePlayerPositionQuery";
import { useAppStore } from "../../../store/appStore";
import { type Blueprint, useBlueprintQuery } from "../useBlueprintQuery";
import { usePreviousNonNull } from "../usePreviousNonNull";
import { useSelectedObjectTypeQuery } from "../useSelectedObjectTypeQuery";

export type Vec3 = readonly [number, number, number];

type BlueprintCommonReturn = {
  dustClient: ReturnType<typeof useDustClient>["data"];
  userEntityId: string | undefined;

  cursorPosition: Vec3;
  playerPosition: Vec3 | undefined | null;
  playerChunkPosition: Vec3;
  selectedObjectType: number | null | undefined;

  blueprint: Blueprint | undefined;
  isBlueprintLoading: boolean;

  forceField: ReturnType<typeof useForceFieldQuery>["data"];
  expandForceField: ReturnType<typeof useMutation<unknown, Error, void>>;

  handleAddWaypoint: () => Promise<void>;

  isTooFarFromCity: boolean;
  isExpandingForceField: boolean;
  forceFieldError: string | null;
  isEnemyTerritory: boolean;
};

export function useBlueprintCommon(): BlueprintCommonReturn {
  const { data: dustClient } = useDustClient();
  const userAddress = dustClient?.appContext.userAddress;
  const userEntityId = userAddress ? encodePlayer(userAddress) : undefined;

  const { autoSwitch, matchInventory } = useAppStore();

  const { data: cursor } = useCursorQuery();
  const prevCursor = usePreviousNonNull(cursor);
  const cursorPosition = useMemo(() => {
    return [
      cursor?.[0] ?? prevCursor?.[0] ?? 0,
      cursor?.[1] ?? prevCursor?.[1] ?? 0,
      cursor?.[2] ?? prevCursor?.[2] ?? 0,
    ] satisfies Vec3;
  }, [cursor, prevCursor]);

  const { data: playerPosition } = usePlayerPositionQuery();
  const { data: selectedObjectType } = useSelectedObjectTypeQuery(autoSwitch);

  const playerChunkPosition = useMemo(() => {
    return [
      Math.floor((playerPosition?.[0] ?? 0) / 16),
      Math.floor((playerPosition?.[1] ?? 0) / 16),
      Math.floor((playerPosition?.[2] ?? 0) / 16),
    ] satisfies Vec3;
  }, [playerPosition]);

  const { data: blueprint, isLoading: isBlueprintLoading } = useBlueprintQuery({
    playerPosition: playerChunkPosition,
    cursorPosition,
  });

  const { data: inventory } = usePlayerInventoryQuery();
  const filteredBlueprint = useMemo(() => {
    if (!matchInventory) {
      return blueprint;
    }

    const inventoryItemsMap = inventory?.reduce(
      (acc, item) => {
        acc[item.objectTypeId] = true;
        return acc;
      },
      {} as Record<number, boolean>,
    );

    return blueprint?.filter((item) => {
      if (item.objectTypeId === 1) {
        return true;
      }

      if (matchInventory) {
        return inventoryItemsMap?.[item.objectTypeId] ?? false;
      }
    });
  }, [blueprint, inventory, matchInventory]);

  const {
    data: forceField,
    refetch: refetchForceField,
    isLoading: isForceFieldLoading,
  } = useForceFieldQuery(
    playerPosition?.[0] ?? 0,
    playerPosition?.[1] ?? 0,
    playerPosition?.[2] ?? 0,
  );

  const expandForceField = useMutation({
    mutationFn: async () => {
      if (!dustClient || !blueprint || blueprint.length === 0) return;
      if (!forceField || forceField.current.forceField) {
        return;
      }
      if (!forceField.adjacent) {
        throw new Error(
          "No adjacent force field found. Cannot expand force field.",
        );
      }
      const userAddress = dustClient.appContext.userAddress;
      const userEntityId = encodePlayer(userAddress);

      const result = await dustClient.provider.request({
        method: "systemCall",
        params: [
          {
            systemId: resourceToHex({
              type: "system",
              namespace: "",
              name: "ForceFieldSystem",
            }),
            abi: IWorldAbi,
            functionName: "addFragment",
            args: [
              userEntityId,
              cityForceFieldId,
              packVec3(forceField.adjacent.fragmentPos),
              packVec3(forceField.current.fragmentPos),
              "0x",
            ],
          },
        ],
      });
      console.log("addFragment result", result);

      const errorMessage = decodeTransactionError(IWorldAbi, result);
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      refetchForceField();
      return result;
    },
  });

  useEffect(() => {
    if (expandForceField.isPending) return;
    if (isBlueprintLoading || isForceFieldLoading) return;
    if (!dustClient || !blueprint || blueprint.length === 0) return;
    if (!forceField || !forceField.adjacent || forceField.current.forceField) {
      return;
    }

    expandForceField.mutate();
  }, [
    dustClient,
    blueprint,
    forceField,
    expandForceField,
    isBlueprintLoading,
    isForceFieldLoading,
  ]);

  const handleAddWaypoint = async () => {
    if (!dustClient) {
      throw new Error("Could not connect to Dust client");
    }
    console.log("requesting marker");
    await dustClient.provider.request({
      method: "setWaypoint",
      params: {
        entity:
          "0x030000023900000093fffffa1100000000000000000000000000000000000000",
        label: "Permutation City",
      },
    });
  };

  const isTooFarFromCity = !blueprint || blueprint.length === 0;
  const isExpandingForceField = expandForceField.isPending;
  const forceFieldError = expandForceField.error?.message || null;
  const isEnemyTerritory =
    forceField?.current.forceField?.forceFieldId !== undefined &&
    forceField?.current.forceField?.forceFieldId !== cityForceFieldId;

  return {
    dustClient,
    userEntityId,

    cursorPosition,
    playerPosition,
    playerChunkPosition,
    selectedObjectType,

    blueprint: filteredBlueprint,
    isBlueprintLoading,

    forceField,
    expandForceField,

    handleAddWaypoint,

    isTooFarFromCity,
    isExpandingForceField,
    forceFieldError,
    isEnemyTerritory,
  };
}
